/**
 * AdminDashboardTab — Overview cards for the Super Admin landing view.
 *
 * Pulls every number live from `/api/admin/stats` — no hardcoded values.
 * Renders 4 primary KPI cards (Total Businesses, Founding Members, Total
 * Orders, Website Visits) plus a small activity strip. Explicitly shows
 * "—" when a metric is `null` (e.g. `website_visits_30d` before we start
 * recording page-views) rather than faking a number.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Store, Trophy, ShoppingBag, BarChart3, Loader2 } from "lucide-react";
import { API } from "@/App";

function KpiCard({ icon: Icon, value, label, sub, testid }) {
  return (
    <div
      className="rounded-2xl border border-tbc-navy/10 bg-white p-5 shadow-sm flex items-start gap-4"
      data-testid={testid}
    >
      <div className="w-11 h-11 rounded-xl bg-tbc-cream flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-tbc-gold" />
      </div>
      <div className="min-w-0">
        <p className="font-heading text-3xl text-tbc-navy leading-none">
          {value === null || value === undefined ? "—" : value.toLocaleString?.() ?? value}
        </p>
        <p className="text-xs uppercase tracking-widest text-tbc-navy/50 mt-2">{label}</p>
        {sub && <p className="text-[11px] text-tbc-navy/60 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

export default function AdminDashboardTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        if (!cancelled) setStats(r.data);
      } catch (e) {
        if (!cancelled) setStats({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="py-10 flex items-center justify-center gap-2 text-tbc-navy/50">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading dashboard…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard-tab">
      <div>
        <h2 className="font-heading text-3xl text-tbc-navy">Welcome back.</h2>
        <p className="text-sm text-tbc-navy/60 mt-1">Here's what's happening across TheBlkCommunity.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Store}
          value={stats?.businesses}
          label="Total Businesses"
          testid="admin-kpi-businesses"
        />
        <KpiCard
          icon={Trophy}
          value={stats?.founding_members}
          label="Founding Members"
          sub={stats?.founding_percent != null ? `${stats.founding_percent}% complete` : null}
          testid="admin-kpi-founders"
        />
        <KpiCard
          icon={ShoppingBag}
          value={stats?.orders}
          label="Total Orders"
          sub={stats?.orders_30d != null ? `${stats.orders_30d.toLocaleString()} in last 30 days` : null}
          testid="admin-kpi-orders"
        />
        <KpiCard
          icon={BarChart3}
          value={stats?.website_visits_30d}
          label="Website Visits"
          sub={stats?.website_visits_30d != null ? "Last 30 days" : "Not yet tracked"}
          testid="admin-kpi-visits"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <MiniStat label="Products" value={stats?.products} />
        <MiniStat label="Users" value={stats?.users} />
        <MiniStat label="Articles" value={stats?.articles} />
        <MiniStat label="Pending verifications" value={stats?.pending_verification} tone="warn" />
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone = "" }) {
  return (
    <div className={`rounded-xl border p-3 ${tone === "warn" ? "border-amber-200 bg-amber-50" : "border-tbc-navy/10 bg-tbc-cream/30"}`}>
      <p className="font-heading text-lg text-tbc-navy leading-none">
        {value === null || value === undefined ? "—" : value.toLocaleString?.() ?? value}
      </p>
      <p className="uppercase tracking-widest text-tbc-navy/50 mt-1 text-[10px]">{label}</p>
    </div>
  );
}
